import type { ReactNode } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface FilterChipOption<T extends string> {
  value: T
  label: string
  icon?: ReactNode
}

interface FilterChipProps<T extends string> {
  label: string
  value: T
  onChange: (value: T) => void
  options: FilterChipOption<T>[]
}

export function FilterChip<T extends string>({ label, value, onChange, options }: FilterChipProps<T>) {
  const selected = options.find(o => o.value === value)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            value === options[0]?.value
              ? 'border-border bg-background text-foreground'
              : 'border-primary bg-primary/10 text-primary',
          )}
        >
          {selected?.icon ?? label}
          <ChevronDown className="size-3 opacity-50" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[120px]">
        <DropdownMenuLabel>{label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {options.map((opt) => (
          <DropdownMenuItem
            key={opt.value}
            onSelect={() => onChange(opt.value)}
            className="gap-2"
          >
            <Check className={cn('size-3.5', value === opt.value ? 'opacity-100' : 'opacity-0')} />
            {opt.icon}
            {opt.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
